import { Button, useNotifications } from '@ph-react-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useDenyTermReportById } from '../../../hooks/term-reports/admin/useDenyTermReportById';
import { useModal } from '../../../hooks/useModal';
import type { TermReportDenyFormDto } from '../../../models/term-reports/term-report-deny.dto';
import { Textarea } from '../../../utils/components/hoc-components';
import { DENY_TERM_REPORT } from '../../../utils/constants/term-reports/modals';
import {
  getCommonValidators,
  VALIDATION as V,
} from '../../../utils/inputValidators';
import { Dialog } from '../../shared/components/Dialog';

export const DenyTermReportModal = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const { t } = useTranslation();
  const notification = useNotifications();
  const queryClient = useQueryClient();
  const { close: closeModal } = useModal(DENY_TERM_REPORT);

  const denyTermReport = useDenyTermReportById();

  const methods = useForm({
    defaultValues: {
      comment: '',
    },
  });

  const onSubmit = (data: TermReportDenyFormDto) => {
    denyTermReport
      .mutateAsync({
        id: id!,
        comment: data.comment,
      })
      .then(() => {
        notification.success(t('TERM_REPORTS.MESSAGES.SUCCESSFULLY_DENIED'));
        queryClient.invalidateQueries({
          queryKey: ['term-reports-in-progress'],
        });
        queryClient.invalidateQueries({
          queryKey: ['term-reports'],
        });
        closeModal();
      })
      .catch((resError) => {
        notification.danger(t(`NETWORK_ERRORS.${resError.errors[0]}`));
      });
  };

  return (
    <Dialog
      dialogHeader={t('TERM_REPORTS.MODALS.DENY')}
      onClose={() => {
        closeModal();
      }}
      actionButton={
        <Button onClick={methods.handleSubmit((data) => onSubmit(data))}>
          {t('ACTIONS.CONTINUE')}
        </Button>
      }
    >
      <FormProvider {...methods}>
        <form>
          <div className="dialog__confirm__content">
            <Textarea
              name="comment"
              className="dialog__confirm__content__textarea"
              label={t('TERM_REPORTS.MODALS.DENY_NOTE') ?? ''}
              placeholder={t('TERM_REPORTS.MODALS.DENY_NOTE') ?? ''}
              rules={getCommonValidators([V.MAX_LENGTH_2000, V.REQUIRED])}
            />
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
};
